import { Siren } from "lucide-react";
import type { EngineAdvisory } from "@/services/migrations/engine";

/**
 * Behaviour that WAS carried and that the target performs differently.
 *
 * WHY THIS IS NOT A COVERAGE BUCKET, AND WHY IT SITS ABOVE ONE. Every bucket in the
 * coverage panel is about behaviour that was NOT carried, and so is residue. An advisory
 * is the opposite: nothing was lost, so there is nothing for the parity gate to grade,
 * nothing for a residue code to declare, and NO COVERAGE NUMBER THAT CAN MOVE. A plan can
 * read 100% carried, zero undeclared, zero residue — and still change what the org does.
 *
 * The live example is the scheduled-path double fire. A workflow time trigger is deleted
 * when a record stops meeting the rule's criteria and a fresh timer is queued if it meets
 * them again, so a record that qualifies, disqualifies and re-qualifies fires ONCE. A
 * Flow scheduled path is never un-scheduled: the first path stays pending and the
 * re-qualification schedules a second, so the same record fires TWICE.
 *
 * That divergence lived in a module docstring. The human at the approval gate does not
 * read module docstrings, which is why this renders where they cannot miss it and why
 * the engine's own sentences are printed verbatim rather than summarised.
 */
export function EngineAdvisories({ advisories }: { advisories: EngineAdvisory[] }) {
  if (advisories.length === 0) return null;
  return (
    <div className="space-y-3 rounded-md border border-[hsl(var(--accent-warm))]/45 bg-accent-warm-soft p-3">
      <div className="flex items-center gap-2">
        <Siren className="h-4 w-4 shrink-0 text-accent-warm" />
        <span className="font-mono text-[11px] font-semibold uppercase tracking-wide text-accent-warm">
          carried, but behaves differently ({advisories.length})
        </span>
      </div>
      <p className="max-w-prose text-xs text-accent-warm">
        Nothing below was lost, so no coverage number reflects it. These are things the
        migrated component does that the source did not.
      </p>
      <ul className="space-y-3">
        {advisories.map((advisory) => (
          <li key={`${advisory.code}-${advisory.scope.join(",")}`} className="space-y-1">
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="font-mono text-xs font-semibold text-foreground">
                {advisory.code}
              </span>
              {advisory.severity ? (
                <span className="font-mono text-[10px] uppercase tracking-wide text-accent-warm">
                  {advisory.severity.replace(/_/g, " ")}
                </span>
              ) : null}
            </div>
            {advisory.scope.length > 0 ? (
              <p className="break-words font-mono text-[11px] text-muted-foreground">
                {advisory.scope.join(", ")}
              </p>
            ) : null}
            {/* The engine's sentence, verbatim. Paraphrasing the one thing no coverage
                number can show is where it would get subtly wrong. */}
            <p className="max-w-prose text-xs leading-relaxed text-foreground">{advisory.detail}</p>
            {advisory.manual_step ? (
              <p className="max-w-prose text-xs leading-relaxed text-accent-warm">
                <span className="font-mono uppercase">what to do: </span>
                {advisory.manual_step}
              </p>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
